import * as React from "react";

import { cn } from "../../lib/utils";
import { Card } from "./card";
import { Sparkline } from "./sparkline";

/**
 * StatCard — KPI card for the Institutional Console redesign (issue #234,
 * DESIGN-SPEC.md §4.2).
 *
 * Spec §4.2 (verbatim): "`--surface`, 1px `--border`, r8, p14/16. Micro-label
 * → 22px mono value → 11.5px mono delta line. Optional 96×34 sparkline
 * top-right. Value color: `--text` unless the metric itself is a signed
 * P&L (then positive/negative)."
 *
 * This is a standalone, screen-agnostic component per issue #234's scope
 * ("Screen wiring deferred to per-screen issues") — Dashboard/Holdings/etc.
 * will pass their own label/value/delta/sparkline data in #235+.
 *
 * Tone semantics (`valueTone`/`deltaTone`):
 * - "default": `--text` (spec default for value; used for delta on neutral
 *   metrics like "Liabilities" — see Dashboard.dc.html KPI #3 which colors
 *   its sub-line `--text-faint`, exposed here as "muted").
 * - "positive"/"negative": `--positive`/`--negative` — for signed P&L values
 *   (spec: "unless the metric itself is a signed P&L") and gain/loss deltas.
 * - "muted": `--text-faint` — non-directional secondary line (e.g.
 *   "4.9% of assets" under Liabilities in the mockup).
 *
 * Accessibility (spec §7 "Never color-only: gains/losses always carry
 * sign"): callers are expected to pass pre-formatted `value`/`delta` strings
 * that already include a +/− sign for signed metrics (matching every mockup
 * KPI, e.g. "+€60,918"); this component does not synthesize a sign itself
 * since not every value is signed (e.g. "Net worth" is unsigned even though
 * globally positive).
 *
 * Padding is FIXED asymmetric `py-3.5 px-4` (14px vertical / 16px
 * horizontal) per spec's "p14/16" — no responsive breakpoint change; verified
 * against Dashboard.dc.html:35 and Holdings.dc.html:38-53, both `padding:
 * 14px 16px` at every viewport (round-2 fix, ui-design-expert review F1).
 *
 * The micro-label uses the `stat-label` Tailwind color (bound to
 * `--stat-label`), a token that intentionally resolves to `--text-faint` in
 * `console-dark` but to the darker `--text-muted` in `console-light` —
 * hardcoding `--text-faint` in both themes matched the dark mockup but broke
 * AA (2.91:1) in light, where the mockup uses `--text-muted` (4.29:1)
 * instead. See `styles/themes/console-dark.css` / `console-light.css`
 * `--stat-label` (round-2 fix, ui-design-expert review F2).
 */

// Uses the existing `success`/`destructive` Tailwind semantic classes rather
// than new `positive`/`negative` keys: console-dark.css/console-light.css
// already alias `--color-success-500: var(--positive)` and
// `--color-error-500: var(--negative)` (see styles/themes/console-*.css), so
// `text-success`/`text-destructive` resolve to the exact spec §2.1/§2.2
// `--positive`/`--negative` values on console themes while keeping legacy
// themes' existing green/red untouched — same convention value-display.jsx
// already uses for CurrencyValue/PercentValue colorization.
const TONE_CLASSES = {
  default: "text-foreground",
  positive: "text-success",
  negative: "text-destructive",
  muted: "text-text-faint",
};

const SPARK_TONE_COLORS = {
  default: "var(--accent, var(--color-primary-500))",
  positive: "var(--positive, var(--color-success-500))",
  negative: "var(--negative, var(--color-error-500))",
  muted: "var(--text-faint, var(--color-text-disabled))",
};

export const StatCard = React.forwardRef(function StatCard(
  {
    label,
    value,
    delta,
    valueTone = "default",
    deltaTone = "muted",
    sparklineData,
    sparklineTone,
    className,
    ...props
  },
  ref
) {
  const resolvedSparkTone = sparklineTone || valueTone;

  return (
    <Card
      ref={ref}
      className={cn(
        // Fixed asymmetric padding per spec §2.4/§4.2 "p14/16" — verbatim
        // `padding: 14px 16px` in Dashboard.dc.html:35 / Holdings.dc.html:38-53
        // with NO responsive breakpoint change. (Round-2 fix, ui-design-expert
        // review F1: previously `p-3.5 sm:p-4`, a responsive UNIFORM padding
        // that was wrong at both breakpoints.)
        "flex items-start justify-between gap-2.5 rounded-lg py-3.5 px-4",
        className
      )}
      {...props}
    >
      <div className="min-w-0">
        {/* Micro-label — Archivo 600, 11px, uppercase, tracking 0.06em (spec §2.3/§4.2).
            Color uses the theme-tracking `stat-label` token, NOT `text-faint`
            (round-2 fix, ui-design-expert review F2): the light mockup
            (Dashboard Light.dc.html:60) paints this label in --text-muted,
            not --text-faint — hardcoding --text-faint failed AA in light
            (2.91:1 on white). See styles/themes/console-*.css `--stat-label`. */}
        <div className="truncate text-[11px] font-semibold uppercase tracking-[0.06em] text-stat-label">
          {label}
        </div>
        {/* KPI value — JB Mono 600, 22px (spec §2.3) */}
        <div
          className={cn(
            "mt-[5px] font-mono text-[22px] font-semibold leading-none tabular-nums",
            TONE_CLASSES[valueTone] || TONE_CLASSES.default
          )}
        >
          {value}
        </div>
        {/* Delta line — JB Mono, 11.5px (spec §4.2) */}
        {delta !== undefined && delta !== null && delta !== "" && (
          <div
            className={cn(
              "mt-[3px] font-mono text-[11.5px] tabular-nums",
              TONE_CLASSES[deltaTone] || TONE_CLASSES.muted
            )}
          >
            {delta}
          </div>
        )}
      </div>
      {sparklineData && sparklineData.length > 1 && (
        <Sparkline
          data={sparklineData}
          color={SPARK_TONE_COLORS[resolvedSparkTone] || SPARK_TONE_COLORS.default}
          className="mt-1"
          aria-label={label ? `${label} trend` : "Trend sparkline"}
        />
      )}
    </Card>
  );
});
StatCard.displayName = "StatCard";

export default StatCard;
